import { useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faMagnifyingGlass,
    faBook,
    faLayerGroup,
    faKeyboard,
    faArrowRight,
} from '@fortawesome/free-solid-svg-icons'

interface HeroSectionProps {
    searchQuery: string
    onSearchChange: (query: string) => void
    onSearchSubmit: () => void
    onOpenSearch: () => void
    stats: {
        total_articles: number
        total_categories: number
        total_shortcuts: number
    }
}

const HeroSection = ({
    searchQuery,
    onSearchChange,
    onSearchSubmit,
    onOpenSearch,
    stats,
}: HeroSectionProps) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        const timeout = setTimeout(() => inputRef.current?.focus(), 400)
        return () => clearTimeout(timeout)
    }, [])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') onSearchSubmit()
    }

    return (
        <section className="kb-hero">
            <div className="kb-hero__particles">
                {Array.from({ length: 20 }).map((_, i) => (
                    <span
                        key={i}
                        className="kb-hero__particle"
                        style={{
                            '--delay': `${Math.random() * 6}s`,
                            '--duration': `${8 + Math.random() * 12}s`,
                            '--x-start': `${Math.random() * 100}%`,
                            '--y-start': `${Math.random() * 100}%`,
                            '--size': `${2 + Math.random() * 4}px`,
                            '--opacity': `${0.15 + Math.random() * 0.3}`,
                        } as React.CSSProperties}
                    />
                ))}
            </div>

            <div className="kb-hero__content">
                <div className="kb-hero__badge">
                    <FontAwesomeIcon icon={faBook} className="me-2" />
                    Knowledge Base
                </div>
                <h1 className="kb-hero__title">
                    How can we <span className="kb-hero__highlight">help</span>{' '}
                    you?
                </h1>
                <p className="kb-hero__subtitle">
                    Find answers, guides, and everything you need to master the
                    CMS
                </p>

                <div className="kb-hero__search-wrapper">
                    <div className="kb-hero__search">
                        <FontAwesomeIcon
                            icon={faMagnifyingGlass}
                            className="kb-hero__search-icon"
                        />
                        <input
                            ref={inputRef}
                            type="text"
                            className="kb-hero__search-input"
                            placeholder="Search articles, guides, FAQs..."
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <button
                            className="kb-hero__search-shortcut"
                            onClick={onOpenSearch}
                            title="Open quick search"
                        >
                            <kbd>⌘</kbd>
                            <kbd>K</kbd>
                        </button>
                        {searchQuery && (
                            <button
                                className="kb-hero__search-go"
                                onClick={onSearchSubmit}
                            >
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="kb-hero__stats">
                    <div className="kb-hero__stat">
                        <FontAwesomeIcon
                            icon={faBook}
                            className="kb-hero__stat-icon"
                        />
                        <span className="kb-hero__stat-number">
                            {stats.total_articles}
                        </span>
                        <span className="kb-hero__stat-label">Articles</span>
                    </div>
                    <div className="kb-hero__stat-divider" />
                    <div className="kb-hero__stat">
                        <FontAwesomeIcon
                            icon={faLayerGroup}
                            className="kb-hero__stat-icon"
                        />
                        <span className="kb-hero__stat-number">
                            {stats.total_categories}
                        </span>
                        <span className="kb-hero__stat-label">Categories</span>
                    </div>
                    <div className="kb-hero__stat-divider" />
                    <div className="kb-hero__stat">
                        <FontAwesomeIcon
                            icon={faKeyboard}
                            className="kb-hero__stat-icon"
                        />
                        <span className="kb-hero__stat-number">
                            {stats.total_shortcuts}
                        </span>
                        <span className="kb-hero__stat-label">Shortcuts</span>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HeroSection
